import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { CheckCircle2, CreditCard, Layers, Minus, Plus, Star, Store, Trash2, Truck, Wallet, X } from "lucide-react";
import { toast } from "sonner";
import { api, fmt, formatApiError, STORE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";

const inputCls = "w-full bg-[#050505] border border-white/10 focus:border-red-600/70 rounded-lg px-4 py-3 text-sm text-white placeholder:text-zinc-600 outline-none transition-colors";
const SHIPPING_COST = 12.0;
const FREE_SHIPPING_OVER = 150;

export default function CartDrawer() {
  const { items, subtotal, setQty, remove, clear, open, setOpen } = useCart();
  const { user, refreshUser } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState("cart");
  const [delivery, setDelivery] = useState("spedizione");
  const [shipping, setShipping] = useState({ name: "", address: "", city: "", zip: "" });
  const [pay, setPay] = useState("carta-demo");
  const [terms, setTerms] = useState(false);
  const [usePoints, setUsePoints] = useState(false);
  const [useCredit, setUseCredit] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [couponInput, setCouponInput] = useState("");
  const [coupon, setCoupon] = useState(null);
  const [couponBusy, setCouponBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setStep("cart");
      setResult(null);
      setUsePoints(false);
      setUseCredit(false);
      setDelivery("spedizione");
      setPay("carta-demo");
      setTerms(false);
      setCoupon(null);
      setCouponInput("");
    }
  }, [open]);

  useEffect(() => {
    if (open && user && !shipping.name) {
      setShipping({ name: user.name || "", address: user.address || "", city: user.city || "", zip: user.zip || "" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user]);

  const hasPreorder = items.some((i) => i.product.is_preorder);
  const consignmentSubtotal = items.filter((i) => i.product.is_consignment).reduce((s, i) => s + (i.product.final_price || i.product.price) * i.qty, 0);
  // Sconto bundle (stima lato client; il valore reale lo calcola il server alla creazione ordine)
  const hasBundleItems = items.some((i) => i.product.bundle_name);
  const [bundles, setBundles] = useState([]);
  useEffect(() => {
    if (hasBundleItems) api.get("/products/bundles").then((r) => setBundles(r.data)).catch(() => {});
  }, [hasBundleItems]);
  const cartIds = new Set(items.map((i) => i.product.id));
  const bundleRows = [];
  bundles.forEach((b) => {
    if (b.discount <= 0 || !b.product_ids.every((id) => cartIds.has(id))) return;
    const save = items.filter((i) => b.product_ids.includes(i.product.id)).reduce((s, i) => s + ((i.product.final_price || i.product.price) * i.qty * b.discount) / 100, 0);
    if (save > 0) bundleRows.push({ name: b.name, save: Math.round(save * 100) / 100 });
  });
  const bundleSaving = bundleRows.reduce((s, b) => s + b.save, 0);
  const preorderDaysList = items.filter((i) => i.product.is_preorder && i.product.preorder_expiry_days).map((i) => i.product.preorder_expiry_days);
  const preorderDays = preorderDaysList.length ? Math.min(...preorderDaysList) : null;
  const reserveMode = hasPreorder && pay === "preordine-negozio";
  const couponOk = coupon && consignmentSubtotal < subtotal && (subtotal - consignmentSubtotal) >= (coupon.min_subtotal || 0);
  const discountable = Math.max(0, subtotal - bundleSaving - consignmentSubtotal);
  const couponDiscount = !couponOk ? 0 : coupon.type === "fixed" ? Math.round(Math.min(coupon.value, discountable) * 100) / 100 : coupon.type === "free_shipping" ? 0 : Math.round(discountable * coupon.value) / 100;
  const couponLabel = coupon ? (coupon.type === "fixed" ? `-${fmt(coupon.value)}` : coupon.type === "free_shipping" ? "Spedizione gratuita" : `-${coupon.value}%`) : "";
  const afterCoupon = Math.max(0, discountable - couponDiscount);
  const maxDiscount = user && !reserveMode ? Math.min(Math.floor((user.points_balance || 0) / 100) * 5, afterCoupon) : 0;
  const discount = usePoints && !reserveMode ? maxDiscount : 0;
  const effDelivery = reserveMode ? "ritiro" : delivery;
  const shippingCost = effDelivery === "ritiro" ? 0 : couponOk && coupon.type === "free_shipping" ? 0 : afterCoupon - discount + consignmentSubtotal >= FREE_SHIPPING_OVER ? 0 : SHIPPING_COST;
  const total = Math.max(0, afterCoupon - discount + consignmentSubtotal + shippingCost);
  const creditApplied = useCredit && user && !reserveMode ? Math.min(user.credit || 0, total) : 0;
  const payable = Math.max(0, total - creditApplied);

  // Banner "Crea il tuo bundle": se il carrello contiene articoli idonei, mostra la fascia sconto raggiungibile
  const eligibleSubtotal = items
    .filter((i) => i.product.bundle_eligible && !i.product.is_preorder && !i.product.is_consignment)
    .reduce((s, i) => s + (i.product.final_price || i.product.price) * i.qty, 0);
  const [bundleTiers, setBundleTiers] = useState([]);
  useEffect(() => {
    if (eligibleSubtotal > 0 && open) api.get("/bundle-config").then((r) => setBundleTiers(r.data.tiers || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, eligibleSubtotal > 0]);
  const nextBundleTier = bundleTiers.find((t) => eligibleSubtotal < t.min_total) || null;
  const reachedBundleTier = [...bundleTiers].reverse().find((t) => eligibleSubtotal >= t.min_total) || null;
  const addressOk = shipping.name && shipping.address && shipping.city && shipping.zip;

  const applyCoupon = async () => {
    if (!couponInput.trim()) return;
    if (!user) {
      toast.error("Accedi per usare un codice sconto");
      return;
    }
    setCouponBusy(true);
    try {
      const { data } = await api.post("/discounts/validate", { code: couponInput });
      setCoupon(data);
      toast.success(`Codice ${data.code} applicato: ${data.type === "fixed" ? `-${fmt(data.value)}` : data.type === "free_shipping" ? "spedizione gratuita" : `-${data.value}%`}`);
    } catch (e) {
      setCoupon(null);
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setCouponBusy(false);
    }
  };

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/orders", {
        items: items.map((i) => ({ product_id: i.product.id, qty: i.qty })),
        use_points: usePoints && !reserveMode,
        use_credit: useCredit && !reserveMode,
        delivery_method: effDelivery,
        shipping: effDelivery === "spedizione" ? shipping : null,
        payment_method: pay,
        coupon_code: couponOk ? coupon.code : "",
        origin_url: window.location.origin,
        creator_token: (() => { try { return JSON.parse(localStorage.getItem("creator_ref") || "{}").token || ""; } catch { return ""; } })(),
      });
      if (data.checkout_url) {
        toast.info("Ti porto al pagamento sicuro Stripe...");
        window.location.href = data.checkout_url;
        return;
      }
      setResult(data);
      clear();
      setStep("success");
      refreshUser();
      if (data.status === "preordine_riservato") toast.success(data.preorder_expiry_days ? `Preordine ${data.id} riservato! Paga in negozio entro ${data.preorder_expiry_days} giorni` : `Preordine ${data.id} riservato! Paghi in negozio quando preferisci`);
      else toast.success(data.invoice_number ? `Ordine ${data.id} confermato! +${data.points_earned} punti` : `Ordine ${data.id} confermato! Punti al pagamento in cassa`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const methodBtn = (active) =>
    `w-full text-left border rounded-xl p-5 transition-all ${active ? "border-red-600/60 bg-red-600/10" : "border-white/10 hover:border-white/25"}`;

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[60]"
          />
          <motion.aside
            data-testid="cart-drawer"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 300, damping: 32 }}
            className="fixed top-0 right-0 h-full w-full sm:w-[440px] bg-[#0D0D0E] border-l border-white/10 z-[70] flex flex-col"
          >
            <div className="flex items-center justify-between px-6 py-5 border-b border-white/10">
              <h2 className="font-display text-lg font-bold uppercase tracking-tight text-white">
                {step === "cart" && "Carrello"}
                {step === "delivery" && "Consegna"}
                {step === "payment" && "Pagamento demo"}
                {step === "success" && "Ordine confermato"}
              </h2>
              <button data-testid="cart-close-button" onClick={() => setOpen(false)} className="p-2 text-zinc-400 hover:text-white"><X size={18} /></button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-5">
              {step === "cart" && (
                items.length === 0 ? (
                  <p data-testid="cart-empty" className="text-sm text-zinc-500 font-mono text-center mt-16">Il carrello è vuoto.<br />Il vault ti aspetta.</p>
                ) : (
                  <ul className="space-y-4">
                    {items.map((i) => (
                      <li key={i.product.id} data-testid={`cart-item-${i.product.id}`} className="flex gap-4 border border-white/10 rounded-xl p-3">
                        <img src={i.product.image} alt="" className="w-14 h-16 object-cover rounded-md border border-white/10" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-white leading-snug line-clamp-2">{i.product.name}</p>
                          <p className="font-mono text-xs text-red-400 mt-1">+{(i.product.points ?? Math.floor((i.product.final_price || i.product.price) / 5)) * i.qty} pt</p>
                          {i.product.is_consignment && (
                            <p className="text-[9px] font-mono uppercase tracking-widest font-bold text-amber-400 mt-1">Usato — conto vendita • no reso, no sconti</p>
                          )}
                          <div className="flex items-center justify-between mt-2">
                            {i.product.is_consignment ? (
                              <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 border border-white/10 rounded-full px-3 py-1">Pezzo unico</span>
                            ) : (
                              <div className="flex items-center border border-white/15 rounded-full">
                                <button data-testid={`cart-minus-${i.product.id}`} onClick={() => setQty(i.product.id, i.qty - 1)} className="p-1.5 text-zinc-400 hover:text-white"><Minus size={12} /></button>
                                <span className="font-mono text-xs font-bold w-6 text-center">{i.qty}</span>
                                <button data-testid={`cart-plus-${i.product.id}`} onClick={() => setQty(i.product.id, i.qty + 1)} disabled={i.qty >= (i.product.in_stock ?? 0)} title={i.qty >= (i.product.in_stock ?? 0) ? "Disponibilità massima raggiunta" : ""} className="p-1.5 text-zinc-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"><Plus size={12} /></button>
                              </div>
                            )}
                            <span className="font-mono text-sm font-bold text-white">{fmt((i.product.final_price || i.product.price) * i.qty)}</span>
                          </div>
                        </div>
                        <button data-testid={`cart-remove-${i.product.id}`} onClick={() => remove(i.product.id)} className="self-start p-1 text-zinc-600 hover:text-red-500"><Trash2 size={15} /></button>
                      </li>
                    ))}
                  </ul>
                )
              )}

              {step === "delivery" && (
                <div className="space-y-4">
                  <button data-testid="delivery-spedizione" onClick={() => { setDelivery("spedizione"); setPay("carta-demo"); }} className={methodBtn(delivery === "spedizione")}>
                    <div className="flex items-center gap-3">
                      <Truck size={18} className={delivery === "spedizione" ? "text-red-500" : "text-zinc-400"} />
                      <p className="text-sm font-semibold text-white">Spedizione a domicilio</p>
                    </div>
                    <p className="text-xs text-zinc-500 mt-2">Corriere espresso 24/48h — il costo è calcolato in cassa in base al metodo scelto</p>
                  </button>
                  <button data-testid="delivery-ritiro" onClick={() => setDelivery("ritiro")} className={methodBtn(delivery === "ritiro")}>
                    <div className="flex items-center gap-3">
                      <Store size={18} className={delivery === "ritiro" ? "text-red-500" : "text-zinc-400"} />
                      <p className="text-sm font-semibold text-white">Ritiro in negozio</p>
                    </div>
                    <p className="text-xs text-zinc-500 mt-2">Gratis — {STORE.address}</p>
                  </button>

                  {delivery === "spedizione" && (
                    <div className="space-y-3 pt-2">
                      <input data-testid="checkout-name-input" value={shipping.name} onChange={(e) => setShipping({ ...shipping, name: e.target.value })} placeholder="Nome e cognome" className={inputCls} />
                      <input data-testid="checkout-address-input" value={shipping.address} onChange={(e) => setShipping({ ...shipping, address: e.target.value })} placeholder="Indirizzo" className={inputCls} />
                      <div className="grid grid-cols-2 gap-3">
                        <input data-testid="checkout-city-input" value={shipping.city} onChange={(e) => setShipping({ ...shipping, city: e.target.value })} placeholder="Città" className={inputCls} />
                        <input data-testid="checkout-zip-input" value={shipping.zip} onChange={(e) => setShipping({ ...shipping, zip: e.target.value })} placeholder="CAP" className={inputCls} />
                      </div>
                    </div>
                  )}
                  {delivery === "ritiro" && (
                    <p className="text-xs text-zinc-500 leading-relaxed border border-white/10 rounded-lg p-4">
                      Ti avvisiamo appena l'ordine è pronto. Ritiro presso {STORE.name}, {STORE.address} — {STORE.hours}.
                    </p>
                  )}
                </div>
              )}

              {step === "payment" && (
                <div className="space-y-4">
                  {hasPreorder && (
                    <p className="text-[11px] font-mono uppercase tracking-wider text-amber-400 border border-amber-500/30 bg-amber-500/10 rounded-lg p-3">
                      Preordine: paga ora online oppure prenota e paghi in negozio{preorderDays ? ` entro ${preorderDays} giorni` : " quando preferisci (senza scadenza)"}
                    </p>
                  )}
                  {(hasPreorder
                    ? [
                        { id: "stripe", icon: CreditCard, title: "Paga ora online", desc: "Carta, Apple Pay, Google Pay, PayPal, Klarna — pagamento sicuro Stripe." },
                        { id: "carta-demo", icon: CreditCard, title: "Carta demo", desc: "Pagamento simulato — nessun addebito reale." },
                        { id: "preordine-negozio", icon: Store, title: "Prenota, paga in negozio", desc: preorderDays ? `Ritiro in store: hai ${preorderDays} giorni per pagare, poi la prenotazione scade.` : "Ritiro in store: paghi quando passi in negozio, senza scadenza." },
                      ]
                    : [
                        { id: "stripe", icon: CreditCard, title: "Paga online", desc: "Carta, Apple Pay, Google Pay, PayPal, Klarna — pagamento sicuro Stripe." },
                        { id: "carta-demo", icon: CreditCard, title: "Carta demo", desc: "Pagamento simulato — nessun addebito reale." },
                        ...(delivery === "ritiro" ? [{ id: "in-cassa", icon: Store, title: "Paga in cassa al ritiro", desc: "Paghi direttamente in negozio quando ritiri l'ordine." }] : []),
                      ]
                  ).map((m) => (
                    <button
                      key={m.id}
                      data-testid={`payment-method-${m.id}`}
                      onClick={() => { setPay(m.id); if (m.id === "preordine-negozio") setDelivery("ritiro"); }}
                      className={methodBtn(pay === m.id)}
                    >
                      <div className="flex items-center gap-3">
                        <m.icon size={18} className={pay === m.id ? "text-red-500" : "text-zinc-400"} />
                        <p className="text-sm font-semibold text-white">{m.title}</p>
                      </div>
                      <p className="text-xs text-zinc-500 mt-2">{m.desc}</p>
                    </button>
                  ))}

                  <div className="pt-2">
                    <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-600 mb-2">Codice sconto</p>
                    {coupon ? (
                      <div data-testid="coupon-applied" className="flex items-center justify-between border border-emerald-600/40 bg-emerald-600/10 rounded-lg px-4 py-3">
                        <span className="text-xs font-mono font-bold text-emerald-400">{coupon.code} — {couponLabel}</span>
                        <button data-testid="coupon-remove-button" onClick={() => { setCoupon(null); setCouponInput(""); }} className="text-zinc-500 hover:text-red-400 transition-colors"><X size={13} /></button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <input
                          data-testid="coupon-input"
                          value={couponInput}
                          onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                          placeholder="WELCOME10"
                          maxLength={24}
                          className={inputCls}
                        />
                        <button data-testid="coupon-apply-button" onClick={applyCoupon} disabled={couponBusy || !couponInput.trim()} className="shrink-0 border border-white/20 hover:border-red-600 disabled:opacity-40 text-white text-xs font-display font-bold uppercase tracking-wide px-5 rounded-lg transition-all">
                          Applica
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {step === "success" && result && (
                <div data-testid="order-success" className="text-center pt-10">
                  <CheckCircle2 size={48} className="text-emerald-400 mx-auto mb-6" />
                  <p className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-500">Numero ordine</p>
                  <p data-testid="order-id" className="font-mono font-bold text-2xl text-red-500 mt-2">{result.id}</p>
                  <p data-testid="order-delivery" className="text-xs text-zinc-400 mt-3">
                    {result.delivery_method === "ritiro"
                      ? `Ritiro in negozio: ${STORE.address}`
                      : "Spedizione all'indirizzo indicato"}
                  </p>
                  <div className="border border-white/10 rounded-xl p-5 mt-6 text-left space-y-2">
                    <div className="flex justify-between text-sm"><span className="text-zinc-400">Totale</span><span className="font-mono font-bold text-white">{fmt(result.total)}</span></div>
                    {result.shipping_cost > 0 && <div className="flex justify-between text-sm"><span className="text-zinc-400">Di cui spedizione</span><span className="font-mono text-zinc-300">{fmt(result.shipping_cost)}</span></div>}
                    {result.coupon_discount > 0 && <div className="flex justify-between text-sm"><span className="text-zinc-400">Coupon {result.coupon_code}</span><span className="font-mono text-emerald-400">-{fmt(result.coupon_discount)}</span></div>}
                    {result.bundle_discount > 0 && <div data-testid="order-bundle-discount" className="flex justify-between text-sm"><span className="text-zinc-400">Sconto bundle{(result.bundle_names || []).length ? ` (${result.bundle_names.join(", ")})` : ""}</span><span className="font-mono text-emerald-400">-{fmt(result.bundle_discount)}</span></div>}
                    {result.coupon_code && result.coupon_discount === 0 && <div className="flex justify-between text-sm"><span className="text-zinc-400">Coupon {result.coupon_code}</span><span className="font-mono text-emerald-400">Spedizione gratuita</span></div>}
                    {result.credit_used > 0 && <div data-testid="order-credit-used" className="flex justify-between text-sm"><span className="text-zinc-400">Credito Wanted usato</span><span className="font-mono text-emerald-400">-{fmt(result.credit_used)}</span></div>}
                    {result.credit_used > 0 && <div className="flex justify-between text-sm"><span className="text-zinc-400">{result.payment_method === "in-cassa" ? "Da pagare in cassa" : "Pagato con carta"}</span><span className="font-mono font-bold text-white">{fmt(result.paid_amount)}</span></div>}
                    {result.points_spent > 0 && <div className="flex justify-between text-sm"><span className="text-zinc-400">Punti utilizzati</span><span className="font-mono text-red-400">-{result.points_spent} pt</span></div>}
                    <div className="flex justify-between text-sm"><span className="text-zinc-400">{result.invoice_number ? "Punti guadagnati" : "Punti in attesa (accredito al ritiro)"}</span><span className="font-mono font-bold text-emerald-400">+{result.points_earned} pt</span></div>
                  </div>
                  {result.status === "preordine_riservato" && (
                    <div data-testid="preorder-reserved-note" className="border border-amber-500/30 bg-amber-500/10 rounded-xl p-4 mt-4 text-left">
                      <p className="text-xs font-mono uppercase tracking-wider text-amber-400 font-bold">Preordine riservato</p>
                      <p className="text-xs text-zinc-300 mt-2 leading-relaxed">
                        {result.expires_at
                          ? `Paga in negozio entro il ${new Date(result.expires_at).toLocaleDateString("it-IT", { day: "2-digit", month: "long" })} (${result.preorder_expiry_days} giorni), poi la prenotazione scade e la copia torna disponibile.`
                          : "Passa in negozio a pagare quando preferisci: questa prenotazione non ha scadenza."}
                      </p>
                    </div>
                  )}
                  <p className="text-xs text-zinc-500 mt-6">
                    {result.invoice_number
                      ? "Fattura e punti sono già sul tuo profilo."
                      : "Fattura e punti ti aspettano al pagamento in cassa."}
                  </p>
                </div>
              )}
            </div>

            {step !== "success" && items.length > 0 && (
              <div className="border-t border-white/10 px-6 py-5 space-y-4">
                {user && maxDiscount > 0 && !reserveMode && (
                  <button
                    data-testid="apply-points-button"
                    onClick={() => setUsePoints(!usePoints)}
                    className={`w-full flex items-center justify-between border rounded-xl px-4 py-3 transition-all ${usePoints ? "border-red-600/60 bg-red-600/10" : "border-white/10 hover:border-white/25"}`}
                  >
                    <span className="flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-zinc-300">
                      <Star size={13} className="text-red-500 fill-red-500" /> Usa punti (100 pt = 5€)
                    </span>
                    <span className="font-mono text-xs font-bold text-red-400">-{fmt(maxDiscount)}</span>
                  </button>
                )}
                {user && (user.credit || 0) > 0 && !reserveMode && (
                  <button
                    data-testid="apply-credit-button"
                    onClick={() => setUseCredit(!useCredit)}
                    className={`w-full flex items-center justify-between border rounded-xl px-4 py-3 transition-all ${useCredit ? "border-emerald-600/60 bg-emerald-600/10" : "border-white/10 hover:border-white/25"}`}
                  >
                    <span className="flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-zinc-300">
                      <Wallet size={13} className="text-emerald-400" /> Usa Credito Wanted
                    </span>
                    <span className="font-mono text-xs font-bold text-emerald-400">-{fmt(Math.min(user.credit || 0, total))}</span>
                  </button>
                )}
                <div className="space-y-1.5 text-sm">
                  {eligibleSubtotal > 0 && bundleTiers.length > 0 && (
                    <div data-testid="cart-bundle-tier-banner" className="border border-emerald-500/25 bg-emerald-500/[0.05] rounded-xl p-3.5 mb-3">
                      <p className="text-[10px] font-mono uppercase tracking-widest font-bold text-emerald-300 flex items-center gap-1.5">
                        <Layers size={11} /> Hai {fmt(eligibleSubtotal)} di articoli idonei al bundle
                      </p>
                      {nextBundleTier ? (
                        <>
                          <p className="text-xs text-zinc-400 mt-1.5 leading-relaxed">
                            Ti mancano {fmt(nextBundleTier.min_total - eligibleSubtotal)} per lo sconto del {nextBundleTier.percent}%.
                          </p>
                          <div className="h-1.5 rounded-full bg-white/10 overflow-hidden mt-2">
                            <div className="h-full bg-emerald-400 rounded-full transition-all duration-300" style={{ width: `${Math.min(100, (eligibleSubtotal / nextBundleTier.min_total) * 100)}%` }} />
                          </div>
                        </>
                      ) : reachedBundleTier ? (
                        <p className="text-xs text-emerald-300/90 mt-1.5 leading-relaxed">
                          Sei nella fascia -{reachedBundleTier.percent}%: componili come bundle per ottenere lo sconto.
                        </p>
                      ) : null}
                      <button data-testid="cart-bundle-tier-cta" onClick={() => { setOpen(false); nav("/bundle"); }} className="mt-2.5 text-[10px] font-display font-bold uppercase tracking-widest text-emerald-300 hover:text-emerald-200 underline underline-offset-4 transition-colors">
                        Apri Crea il tuo bundle
                      </button>
                    </div>
                  )}
                  <div className="flex justify-between text-zinc-400"><span>Subtotale</span><span className="font-mono">{fmt(subtotal)}</span></div>
                  {bundleRows.map((b) => (
                    <div key={b.name} data-testid={`cart-bundle-${b.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="flex justify-between text-zinc-400"><span>Sconto bundle "{b.name}"</span><span className="font-mono text-emerald-400">-{fmt(b.save)}</span></div>
                  ))}
                  {couponOk && <div data-testid="cart-coupon-line" className="flex justify-between text-emerald-400"><span>Coupon {coupon.code}</span><span className="font-mono">{coupon.type === "free_shipping" ? "Sped. gratuita" : `-${fmt(couponDiscount)}`}</span></div>}
                  {discount > 0 && <div className="flex justify-between text-red-400"><span>Sconto punti</span><span className="font-mono">-{fmt(discount)}</span></div>}
                  {creditApplied > 0 && <div data-testid="cart-credit-line" className="flex justify-between text-emerald-400"><span>Credito Wanted</span><span className="font-mono">-{fmt(creditApplied)}</span></div>}
                  {step !== "cart" && (
                    <div className="flex justify-between text-zinc-400">
                      <span>{effDelivery === "ritiro" ? "Ritiro in negozio" : "Spedizione"}</span>
                      <span className="font-mono">{shippingCost === 0 ? "Gratis" : fmt(shippingCost)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold text-white text-base pt-1"><span>{creditApplied > 0 ? "Da pagare ora" : "Totale"}</span><span data-testid="cart-total" className="font-mono">{fmt(payable)}</span></div>
                  <p className="text-xs font-mono text-emerald-400 text-right">Guadagni +{Math.floor(total / 5)} punti</p>
                </div>

                {step === "cart" && (
                  user ? (
                    <button data-testid="checkout-start-button" onClick={() => setStep("delivery")} className="w-full bg-red-600 hover:bg-red-700 text-white font-display font-bold uppercase tracking-wide text-sm px-6 py-4 rounded-full transition-all active:scale-[0.98]">
                      Procedi al checkout
                    </button>
                  ) : (
                    <button data-testid="checkout-login-button" onClick={() => { setOpen(false); nav("/login", { state: { from: "/" } }); }} className="w-full bg-red-600 hover:bg-red-700 text-white font-display font-bold uppercase tracking-wide text-sm px-6 py-4 rounded-full transition-all active:scale-[0.98]">
                      Accedi per ordinare
                    </button>
                  )
                )}
                {step === "delivery" && (
                  <button
                    data-testid="checkout-continue-button"
                    onClick={() => setStep("payment")}
                    disabled={effDelivery === "spedizione" && !addressOk}
                    className="w-full bg-red-600 hover:bg-red-700 disabled:opacity-40 text-white font-display font-bold uppercase tracking-wide text-sm px-6 py-4 rounded-full transition-all active:scale-[0.98]"
                  >
                    Continua al pagamento
                  </button>
                )}
                {step === "payment" && (
                  <>
                  <label className="flex items-start gap-3 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      data-testid="checkout-terms-checkbox"
                      checked={terms}
                      onChange={(e) => setTerms(e.target.checked)}
                      className="mt-0.5 w-4 h-4 accent-red-600 shrink-0"
                    />
                    <span className="text-xs text-zinc-400 leading-relaxed">
                      Ho letto e accetto i{" "}
                      <a href="/termini" target="_blank" rel="noopener noreferrer" data-testid="checkout-terms-link" className="text-red-400 underline underline-offset-2 hover:text-red-300">
                        Termini e Condizioni di vendita
                      </a>{" "}
                      (spedizioni, recesso 14 giorni, garanzia).
                    </span>
                  </label>

                  <button
                    data-testid="checkout-submit-button"
                    onClick={submit}
                    disabled={busy || !terms}
                    className="w-full bg-red-600 hover:bg-red-700 disabled:opacity-40 text-white font-display font-bold uppercase tracking-wide text-sm px-6 py-4 rounded-full transition-all active:scale-[0.98]"
                  >
                    {busy ? "Elaborazione..." : payable === 0 ? "Conferma ordine — coperto da punti/credito" : `Conferma ordine — ${fmt(payable)}`}
                  </button>
                  </>
                )}
              </div>
            )}

            {step === "success" && (
              <div className="border-t border-white/10 px-6 py-5">
                <button data-testid="order-done-button" onClick={() => setOpen(false)} className="w-full border border-white/20 hover:border-red-600 text-white font-display font-bold uppercase tracking-wide text-sm px-6 py-4 rounded-full transition-all">
                  Torna allo shop
                </button>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
